import {Separator, showNotification} from 'platform/components';
import {Box, HStack, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect} from 'react';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {
  ArticleFolderTree,
  DmsDataGrid,
  WarehouseTabExternalFilters,
  catchUnhandledDataGridActions,
  logError,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId, useLatestValue} from 'shared/web';

import type {
  ActionCallback,
  ExternalFilters,
  FormToDataGridConnectorProps,
  QueryFilterObject,
} from 'features/eag-grid';

function isExternalFilters(value: unknown): value is ExternalFilters {
  return typeof value === 'object' && value !== null;
}

const REPLACE_ARTICLE_EXTERNAL_FILTER_ID = 'replace-article-external-filter';

interface ReplaceArticleWarehouseTabProps extends RequiredTestIdProps {
  warehouseId: string | Nullish;
  authorizationProfileId: string | Nullish;
  receiveNoteId: string | Nullish;
  onSelectArticle: (articleId: string) => void;
  onActiveFolderIdChange?: (activeFolderId?: string) => void;
  onExternalFilterChange: (filters: ExternalFilters) => void;
  hasPendingRequest?: boolean;
}

export function ReplaceArticleWarehouseTab(props: ReplaceArticleWarehouseTabProps) {
  // The receive-note-basket-article grid definition does not declare an
  // availableWarehousesList column, so injecting that filter makes the V6 grid
  // engine reject the query (PKG-DG-0004). This tab is not warehouse-scoped
  // (it lists articles across all warehouses), so no whitelist is needed (T20-139044).
  const [folderTreeRef, dataGridModifier, activeFolderId] = useWarehouseDataGridTreeFolder();

  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest ?? false);

  useEffect(
    function notifyActiveFolderIdChange() {
      props.onActiveFolderIdChange?.(activeFolderId);
    },
    [activeFolderId] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const warehouseActionCallback: ActionCallback = ({actionKey, rowId}) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const warehouseArticleId = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(warehouseArticleId)) {
      return logError('Row id is not defined');
    }

    return match(actionKey)
      .with('toBasket', () => {
        props.onSelectArticle(warehouseArticleId);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const externalFiltersTestId = suffixTestId('dg-external-filters', props);

  const filterComponent = (datagridProps: FormToDataGridConnectorProps) => (
    <WarehouseTabExternalFilters
      hideWarehouseField
      data-testid={externalFiltersTestId}
      {...datagridProps}
    />
  );

  const queryModifier = (query: QueryFilterObject): QueryFilterObject => {
    const baseQuery = dataGridModifier?.queryModifier
      ? dataGridModifier.queryModifier(query)
      : query;

    if (isNil(props.warehouseId)) {
      return baseQuery;
    }

    return {
      ...baseQuery,
      warehouseId: props.warehouseId,
    };
  };

  return (
    <HStack height="100%" data-testid={props['data-testid']}>
      <Box flex={1}>
        <VStack height="100%">
          <Box>
            <div id={REPLACE_ARTICLE_EXTERNAL_FILTER_ID}></div>
          </Box>
          <Space vertical={8} />
          <ArticleFolderTree
            ref={folderTreeRef}
            data-testid={suffixTestId('articleFolderTree', props)}
          />
        </VStack>
      </Box>

      <Separator orientation="vertical" />

      <Box flex={3}>
        <DmsDataGrid
          // DG must be re-rendered on folder change to update the query modifier
          // eslint-disable-next-line eslint-js/no-restricted-syntax
          key={dataGridModifier?.key}
          gridCode="receive-note-basket-article"
          actionCallback={warehouseActionCallback}
          externalFilterId={REPLACE_ARTICLE_EXTERNAL_FILTER_ID}
          filterComponent={filterComponent}
          queryModifier={queryModifier}
          onFilterChanged={(filters) => {
            if (isExternalFilters(filters)) {
              props.onExternalFilterChange(filters);
            }
          }}
          data-testid={suffixTestId('dataGrid', props)}
        />
      </Box>
    </HStack>
  );
}
